import React, { useEffect, useState } from 'react';
import { Mail, Send, User } from 'lucide-react';
import { resourceAPI } from '../../services/api';

const AskExpertResource = () => {
  const [question, setQuestion] = useState('');
  const [manager, setManager] = useState(null);
  const [managerLoading, setManagerLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadManager = async () => {
      setManagerLoading(true);
      try {
        const { data } = await resourceAPI.getAccountManager();
        if (!cancelled && data?.assigned) {
          setManager(data.manager);
        }
      } catch {
        // Fehler bewusst leise behandeln; wir zeigen unten eine neutrale Meldung.
      } finally {
        if (!cancelled) setManagerLoading(false);
      }
    };

    loadManager();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!question.trim()) {
      setError('Bitte gib zuerst eine Frage ein.');
      return;
    }

    try {
      setSubmitting(true);
      await resourceAPI.askExpertQuestion(question.trim());
      setQuestion('');
      setSuccess('Deine Frage wurde erfolgreich an deinen Account Manager gesendet.');
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          'Die Frage konnte gerade nicht gesendet werden. Bitte versuche es erneut.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-7">
        <h1 className="text-3xl font-bold text-gray-900">Frag einen Expert</h1>
        <div className="w-12 h-1 rounded-full mt-2 mb-3 bg-hrf-coral" />
        <p className="text-gray-500">
          Stell deine Frage direkt hier im Portal. Wir leiten sie intern an den zuständigen
          Account Manager weiter.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-7">
        <div className="mb-5 p-4 rounded-lg border border-hrf-navy/15 bg-hrf-navy/5">
          <div className="flex items-center gap-2 text-hrf-navy font-semibold text-sm">
            <User className="h-4 w-4" />
            Zuständiger Account Manager
          </div>

          {managerLoading ? (
            <p className="text-sm text-gray-500 mt-2">Wird geladen…</p>
          ) : manager ? (
            <div className="mt-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-hrf-navy text-white flex items-center justify-center text-xs font-bold">
                {manager.initials || 'AM'}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{manager.display_name}</p>
                <p className="text-xs text-gray-500">{manager.email}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-2">
              Aktuell ist noch kein Account Manager zugeordnet. Bitte melde dich kurz bei uns.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label htmlFor="expert-question" className="block text-sm font-medium text-gray-700">
            Deine Frage
          </label>
          <textarea
            id="expert-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={6}
            placeholder="Was möchtest du wissen?"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy focus:border-hrf-navy transition-colors"
          />

          {error ? (
            <p className="text-sm text-hrf-coral">{error}</p>
          ) : null}
          {success ? (
            <p className="text-sm text-emerald-700">{success}</p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-hrf-navy text-white text-sm font-semibold px-5 py-2.5 hover:bg-hrf-navy-800 transition-colors focus:outline-none focus:ring-2 focus:ring-hrf-navy focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Mail className="h-4 w-4" />
                Wird gesendet…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Frage senden
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskExpertResource;
